import { system } from '@workday/canvas-tokens-web'
import { ReactNode } from 'react'
import { Badge, Box, Button, Flex, Heading, Link, Stack } from '~/design_system'
import { ChevronRight } from '~/icons'
import { Icon } from '~/icons/create-icon'
import { trackedButtonClick, trackedLinkClick } from '~/third_party/analytics'

type BaseQuickActionProps = {
  id: string
} & QuickActionContentProps
export type QuickActionLinkProps = {
  /** The location to navigate to on click. */
  href: string
} & BaseQuickActionProps

export type QuickActionButtonProps = {
  /** The action to perform to on click. */
  onClick: () => void
} & BaseQuickActionProps

export const QuickActionLink = ({ id, title, icon, href, badgeCount, ...props }: QuickActionLinkProps) => {
  return (
    <Link
      data-testid={`quick-action-${id}`}
      variant="unstyled"
      href={href}
      _hover={{ textDecoration: 'none' }}
      aria-label={title}
      onClick={trackedLinkClick(undefined, 'quick_action', {
        quick_action_type: id,
        badge_count: badgeCount || 0
      })}
      {...props}>
      <QuickActionContent title={title} icon={icon} badgeCount={badgeCount} />
    </Link>
  )
}

export const QuickActionButton = ({ id, onClick, badgeCount, icon, title, ...props }: QuickActionButtonProps) => {
  return (
    <Button
      whiteSpace="normal"
      variant="unstyled"
      data-testid={`quick-action-${id}`}
      onClick={trackedButtonClick(onClick, 'quick_action', {
        quick_action_type: id,
        badge_count: badgeCount || 0
      })}
      _hover={{ textDecoration: 'none' }}
      aria-label={title}
      style={{ height: 'max-content' }}
      minH="66px"
      {...props}>
      <QuickActionContent title={title} icon={icon} badgeCount={badgeCount} />
    </Button>
  )
}

type QuickActionContentProps = {
  /** Link Box title. Displayed next to the icon in large, bold text. */
  title?: string
  /** Badge count to show beside the title */
  badgeCount?: number
  icon: ReactNode
}
const QuickActionContent = ({ title, icon, badgeCount }: QuickActionContentProps) => {
  // TODO(VNDLY-64015): Replace these fallbacks with boxShadow="1" and hover boxShadow="2" when sana_rebrand_enabled is retired.
  return (
    <Flex
      layerStyle="card"
      role="group"
      justifyContent="space-between"
      alignItems="center"
      borderRadius="full"
      p={3}
      boxShadow={`var(--vndly-quick-action-shadow, var(${system.depth[1]}))`}
      transition="all 0.2s ease-in-out"
      _hover={{
        boxShadow: `var(--vndly-quick-action-hover-shadow, var(${system.depth[2]}))`,
        cursor: 'pointer',
        textDecoration: 'none'
      }}>
      <Stack isInline spacing={2} alignItems="center">
        {icon}
        <Heading as="h2" fontSize="md" fontWeight="semibold">
          {title}
        </Heading>
        {badgeCount !== undefined && badgeCount !== null && (
          <Badge alignSelf="center" variant="incomplete" size="lg" px={2} background="gray.100">
            {badgeCount}
          </Badge>
        )}
      </Stack>
      <Box as={ChevronRight} w="24px" h="24px" color="gray.400" _groupHover={{ color: 'gray.500' }} />
    </Flex>
  )
}

export type QuickActionIconProps = {
  /** The icon for the card */
  icon: typeof Icon
  /** The color of the icon background box */
  color: string
}

/** A white icon inside of a rounded, colored background box */
export const QuickActionIcon = ({ color, icon, ...props }: QuickActionIconProps) => (
  <Flex
    w={8}
    h={8}
    flexShrink={0}
    color="white"
    bg={color}
    borderRadius="full"
    p={2}
    justifyContent="center"
    alignItems="center"
    {...props}>
    <Box as={icon as any} w="full" h="full" />
  </Flex>
)
